import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { savePost, updatePost } from "../../features/admin/PostsSlice";
import TextEditor from "../TextEditor";
import { toast } from "react-toastify";
import { convertToSlug } from "../../utils/helps";
import ApiCustom from "../../utils/ApiCustom";
import { PhotoIcon } from "@heroicons/react/24/outline";

const postSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(5, "Tiêu đề phải ít nhất 5 ký tự")
    .required("Tiêu đề không được để trống"),

  content: Yup.string()
    .trim()
    .test("content-length", "Nội dung không được để trống", (value) => {
      return value && value.trim().length >= 10;
    })
    .required("Nội dung không được để trống"),

  categoryId: Yup.string().trim().required("Danh mục không được để trống"),

  // images: Yup.mixed(),
});
const PostModal = ({ postId, onClose }) => {
  const [post, setPost] = useState(null);
  const [formData, setFormData] = useState({
    title: post?.title || "",
    content: post?.content || null,
    status: post?.status || "draft",
    seoTitle: post?.seoTitle || "",
    seoDescription: post?.seoDescription || "",
    seoKeywords: post?.seoKeywords || "",
    cateGory: post?.cateGory || "",
    images: post?.images || null,
    imagesUrl: post?.images?.[0]?.url || "",
    categoryId: post?.categoryId || "",
  });
  const [errors, setErrors] = useState({});
  const { categories, status } = useSelector((state) => state.categories);
  const [logoPreview, setLogoPreview] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (postId) {
      getPosts();
    }
  }, []);

  useEffect(() => {
    if (post) {
      setFormData({
        title: post?.title || "",
        content: post?.content || null,
        status: post?.status || "draft",
        seoTitle: post?.seoTitle || "",
        seoDescription: post?.seoDescription || "",
        seoKeywords: post?.seoKeywords || "",
        cateGory: post?.cateGory || "",
        imagesUrl: post?.images?.[0]?.url || "",
        categoryId: post?.categoryId || "",
      });
    }
  }, [post]);

  const getPosts = () => {
    ApiCustom.get(`/posts/${postId}`)
      .then((res) => {
        setPost(res.data?.data);
      })
      .catch((err) => console.log(err));
  };

  const { status: saveStatus, error } = useSelector((state) => state.posts);

  const handleChange = async (e) => {
    const name = e.name || e.target?.name;
    const value = e.value || e.target?.value;
    const files = e.target?.files;
    let newValue = name === "images" && files ? files[0] : value;
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    try {
      if (name === "images" && newValue instanceof File) {
        const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
        if (!allowedTypes.includes(newValue.type)) {
          setErrors((prev) => ({
            ...prev,
            logo: "Chỉ chấp nhận file ảnh (JPEG, PNG, GIF)",
          }));
          return;
        }

        if (newValue.size > 5 * 1024 * 1024) {
          setErrors((prev) => ({
            ...prev,
            images: "Kích thước file không được vượt quá 5MB",
          }));
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
        };
        reader.readAsDataURL(newValue);
        return;
      }

      await Yup.reach(postSchema, name).validate(newValue);
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    } catch (validationError) {
      setErrors((prev) => ({
        ...prev,
        [name]: validationError.message,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const finalFormData = {
        ...formData,
      };
      let validationSchema = postSchema;

      if (post?.id && !formData.images) {
        validationSchema = postSchema.omit(["images"]);
      }
      await validationSchema.validate(finalFormData, { abortEarly: false });
      const submitData = new FormData();
      Object.keys(finalFormData).forEach((key) => {
        const value = finalFormData[key];
        if (value !== null && value !== undefined) {
          submitData.append(key, value);
        }
      });

      submitData.append("slug", convertToSlug(finalFormData?.title));

      try {
        // post?.id
        //   ? await dispatch(
        //       updatePost({ id: post.id, newPost: submitData })
        //     ).unwrap()
        //   : await dispatch(savePost(submitData)).unwrap();
        // toast.success("Thành công!");
        // onClose();
        if (post?.id) {
          setIsSubmitting(true);
          ApiCustom.put(`/posts-update/${post.id}`, submitData, {
            "Content-Type": "application/json",
          })
            .then((res) => {
              toast.success("Cập nhật bài viết thành công!");
              onClose();
            })
            .catch((err) => {
              toast.error(err.response?.data?.message || "Đã xảy ra lỗi!");
            })
            .finally(() => setIsSubmitting(false));
        } else {
          setIsSubmitting(true);
          ApiCustom.post("/blog-posts-create", submitData, {
            "Content-Type": "application/json",
          })
            .then((res) => {
              toast.success("Thêm bài viết thành công!");
              onClose();
            })
            .catch((err) => {
              toast.error(err.response?.data?.message || "Đã xảy ra lỗi!");
            })
            .finally(() => setIsSubmitting(false));
        }
      } catch (error) {
        console.error("Error submitting post:", error);
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError && error.inner) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      }
      setIsSubmitting(false);
    }
  };

  // Determine if button should be in loading state
  const isLoading = isSubmitting || saveStatus === "loading";

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-base-100 rounded-lg w-3/4 max-h-[90vh] overflow-y-auto">
        <div className="flex p-6 sticky top-0 bg-base-100 z-10 pb-4 justify-between items-center">
          <h2 className="text-xl font-semibold mb-4">
            {post ? "Chỉnh sửa" : "Thêm"} bài viết
          </h2>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            disabled={isLoading}
          >
            X
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6">
            <div className="mb-4">
              <label htmlFor="title" className="block text-sm font-medium">
                Tiêu đề<i className="text-red-600"> *</i>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full p-2 border rounded ${
                  errors.title ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="categoryId" className="block text-sm font-medium">
                Danh mục<i className="text-red-600"> *</i>
              </label>
              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option key={0} value="">
                  Chọn danh mục
                </option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="text-red-500 text-sm mt-1">{errors.categoryId}</p>
              )}
            </div>
            <div className="mb-4 ">
              <label className="block text-sm font-medium mb-2">
                Nội dung<i className="text-red-600"> *</i>
              </label>
              <TextEditor
                value={formData.content}
                onChange={handleChange}
                name="content"
                importCustomInsertImage={true}
              />
              {errors.content && (
                <p className="text-red-500 text-sm mt-1">{errors.content}</p>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="images" className="block text-sm font-medium">
                Ảnh bìa<i className="text-red-600"> *</i>
              </label>
              <div className="flex items-center space-x-4">
                <div className="h-20 w-20  mt-2 border-2 border-gray-300 border-dashed rounded-lg overflow-hidden">
                  {logoPreview ? (
                      <img
                        src={`${
                          formData.images
                            ? logoPreview
                            : `${process.env.REACT_APP_API_URL}media/${logoPreview}`
                        }`}
                        alt="Ảnh bìa"
                        className="h-20 w-20 object-contain rounded"
                      />
                  ) : formData.imagesUrl ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}media/${formData.imagesUrl}`}
                      className="h-20 w-20 object-contain rounded"
                    />
                  ) : (
                    <div className="h-20 w-20 flex items-center justify-center text-gray-400">
                      <PhotoIcon className="w-10 h-10 text-gray-300" />
                    </div>
                  )}
                </div>

                <input
                  type="file"
                  name="images"
                  accept="image/jpeg,image/png,image/gif,image/jpg"
                  onChange={handleChange}
                  className="file-input file-input-bordered w-full max-w-xs"
                />
              </div>

              {errors.images && (
                <p className="text-red-500 text-sm mt-1">{errors.images}</p>
              )}
            </div>
            <div className="mb-4">
              <label htmlFor="seoTitle" className="block text-sm font-medium">
                SEO Tiêu đề
              </label>
              <input
                type="text"
                name="seoTitle"
                value={formData.seoTitle}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="seoDescription"
                className="block text-sm font-medium"
              >
                SEO Mô tả
              </label>
              <textarea
                name="seoDescription"
                value={formData.seoDescription}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                rows={2}
              ></textarea>
            </div>
            <div className="mb-4">
              <label
                htmlFor="seoKeywords"
                className="block text-sm font-medium"
              >
                SEO Từ khóa
              </label>
              <input
                type="text"
                name="seoKeywords"
                value={formData.seoKeywords}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder={`Cách nhau bằng dấu phẩy, tối thiểu 3 từ`}
              />
            </div>
            {formData.cateGory && (
              <div className="mb-4">
                <label
                  htmlFor="seoKeywords"
                  className="block text-sm font-medium"
                >
                  Ghi chú
                </label>
                <input
                  type="text"
                  name="seoKeywords"
                  value={formData.cateGory}
                  onChange={handleChange}
                  readOnly
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder=""
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex border-t sticky bottom-0 bg-base-100 z-10 p-6 justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 btn btn-sm rounded"
              disabled={isLoading}
            >
              Hủy
            </button>
            <button
              type="submit"
              className={`px-4 py-2 btn btn-primary rounded btn-sm ${
                isLoading ? "loading" : ""
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-xs mr-2"></span>
                  Đang lưu...
                </>
              ) : (
                "Lưu"
              )}
            </button>
          </div>
          {/* Error Handling */}
          {error && (
            <p className="text-red-500 text-sm mt-2">
              Đã xảy ra lỗi: {error.message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default PostModal;
